/**
 * TodoTaskActionSheet - Dropdown menu anchored to the ellipsis button.
 * Provides color picker, "High Priority", "Schedule as Event", and "Delete" actions.
 */

import React, { useCallback, useEffect, useMemo } from 'react';
import { Alert, Dimensions, Pressable, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  Easing,
} from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { IconSymbol } from '@/components/ui/icon-symbol';
import {
  TASK_NOTE_COLORS,
  DEFAULT_TASK_NOTE_COLOR,
  DARK_MODE_DISPLAY_COLORS,
} from '@/components/shared/fields/StickyNoteColorPicker';
import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { Task } from '@/types/tasks';
import type { ActionMenuAnchor } from './TodoTaskItem';

type TodoTaskActionSheetProps = {
  visible: boolean;
  task: Task | null;
  anchor: ActionMenuAnchor | null;
  onDismiss: () => void;
  onScheduleAsEvent: (task: Task) => void;
  onTogglePriority: (taskId: string) => void;
  onDelete: (taskId: string) => void;
  onColorChange?: (taskId: string, color: string) => void;
  onRequestMoveToList?: (task: Task) => void;
};

const COLOR_DOT_SIZE = 28;
// Conservative menu-height estimate used to decide whether the menu should
// grow downward from the anchor or flip upward to avoid being clipped by the
// bottom tab bar. Real height varies with which optional rows render, but the
// extra padding here is harmless when there's space, and prevents clipping
// when there isn't.
const ESTIMATED_MENU_HEIGHT = 320;
// Vertical buffer that we treat as "off-limits" near the bottom of the screen
// (custom tab bar + floating QuickAddBar). If the menu would land in here,
// flip it upward instead.
const BOTTOM_OBSTRUCTION_HEIGHT = 90;

export function TodoTaskActionSheet({
  visible,
  task,
  anchor,
  onDismiss,
  onScheduleAsEvent,
  onTogglePriority,
  onDelete,
  onColorChange,
  onRequestMoveToList,
}: TodoTaskActionSheetProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const insets = useSafeAreaInsets();

  const scale = useSharedValue(0);
  const opacity = useSharedValue(0);

  useEffect(() => {
    const easing = Easing.out(Easing.quad);
    if (visible) {
      scale.value = withTiming(1, { duration: 75, easing });
      opacity.value = withTiming(1, { duration: 75, easing });
    } else {
      scale.value = withTiming(0, { duration: 75, easing });
      opacity.value = withTiming(0, { duration: 75, easing });
    }
  }, [visible, scale, opacity]);

  const menuStyle = useAnimatedStyle(() => ({
    transform: [{ scaleY: scale.value }, { scaleX: scale.value }],
    opacity: opacity.value,
  }));

  const handlePress = useCallback((action: () => void) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    action();
  }, []);

  const handleColorSelect = useCallback((color: string) => {
    if (!task || !onColorChange) return;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    onColorChange(task.id, color);
    onDismiss();
  }, [task, onColorChange, onDismiss]);

  const handleSchedule = useCallback(() => {
    if (!task) return;
    onDismiss();
    onScheduleAsEvent(task);
  }, [task, onDismiss, onScheduleAsEvent]);

  const handleTogglePriority = useCallback(() => {
    if (!task) return;
    onDismiss();
    onTogglePriority(task.id);
  }, [task, onDismiss, onTogglePriority]);

  const handleDelete = useCallback(() => {
    if (!task) return;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    const taskId = task.id;
    const taskTitle = task.title;
    onDismiss();
    Alert.alert(
      'Delete task?',
      taskTitle,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete', style: 'destructive', onPress: () => onDelete(taskId) },
      ],
    );
  }, [task, onDismiss, onDelete]);

  const handleMove = useCallback(() => {
    if (!task || !onRequestMoveToList) return;
    onDismiss();
    onRequestMoveToList(task);
  }, [task, onDismiss, onRequestMoveToList]);

  const screenHeight = Dimensions.get('window').height;
  const flipUpward = useMemo(() => {
    if (!anchor) return false;
    const reservedBottom = insets.bottom + BOTTOM_OBSTRUCTION_HEIGHT;
    return anchor.y + ESTIMATED_MENU_HEIGHT > screenHeight - reservedBottom;
  }, [anchor, insets.bottom, screenHeight]);

  if (!visible || !task || !anchor) return null;

  const currentColor = task.color ?? DEFAULT_TASK_NOTE_COLOR;
  // When flipping upward, anchor the menu's bottom edge 8px above the press
  // point so the menu opens above the ellipsis button instead of below.
  const positionStyle = flipUpward
    ? { bottom: screenHeight - anchor.y + 8 }
    : { top: anchor.y + 8 };
  const transformOrigin = flipUpward ? ('right bottom' as const) : ('right top' as const);

  return (
    <View style={StyleSheet.absoluteFill} pointerEvents="box-none">
      {/* Backdrop above the task card */}
      <Pressable
        onPress={onDismiss}
        style={[styles.backdropTop, { height: anchor.cardTop }]}
      />
      {/* Backdrop below the task card */}
      <Pressable
        onPress={onDismiss}
        style={[styles.backdropBottom, { top: anchor.cardBottom }]}
      />

      {/* Menu */}
      <Animated.View
        style={[
          styles.menu,
          {
            backgroundColor: palette.cardBackground,
            right: 16,
            transformOrigin,
            ...positionStyle,
          },
          menuStyle,
        ]}
      >
        {/* Color picker row */}
        {onColorChange && (
          <>
            <View style={styles.colorPickerRow}>
              {TASK_NOTE_COLORS.map((color) => {
                const displayColor = colorScheme === 'dark'
                  ? (DARK_MODE_DISPLAY_COLORS[color] ?? color)
                  : color;
                return (
                  <TouchableOpacity
                    key={color}
                    onPress={() => handleColorSelect(color)}
                    activeOpacity={0.7}
                    style={[
                      styles.colorDot,
                      { backgroundColor: displayColor },
                      currentColor === color && styles.colorDotSelected,
                    ]}
                  />
                );
              })}
            </View>
            <View style={[styles.separator, { backgroundColor: palette.borderMuted }]} />
          </>
        )}

        <TouchableOpacity
          style={styles.menuItem}
          onPress={() => handlePress(handleTogglePriority)}
          activeOpacity={0.7}
        >
          <IconSymbol
            name="flag"
            size={18}
            color={task.priority === 'high' ? palette.textMuted : palette.danger}
          />
          <Text style={[styles.menuLabel, { color: palette.text }]}>
            {task.priority === 'high' ? 'Remove Priority' : 'High Priority'}
          </Text>
        </TouchableOpacity>

        <View style={[styles.separator, { backgroundColor: palette.borderMuted }]} />

        <TouchableOpacity
          style={styles.menuItem}
          onPress={() => handlePress(handleSchedule)}
          activeOpacity={0.7}
        >
          <IconSymbol name="calendar" size={18} color={palette.primary} />
          <Text style={[styles.menuLabel, { color: palette.text }]}>
            Schedule as Event
          </Text>
        </TouchableOpacity>

        {onRequestMoveToList && (
          <>
            <View style={[styles.separator, { backgroundColor: palette.borderMuted }]} />

            <TouchableOpacity
              style={styles.menuItem}
              onPress={() => handlePress(handleMove)}
              activeOpacity={0.7}
            >
              <IconSymbol name="folder" size={18} color={palette.primary} />
              <Text style={[styles.menuLabel, { color: palette.text }]}>
                Move to list…
              </Text>
            </TouchableOpacity>
          </>
        )}

        <View style={[styles.separator, { backgroundColor: palette.borderMuted }]} />

        <TouchableOpacity
          style={styles.menuItem}
          onPress={() => handlePress(handleDelete)}
          activeOpacity={0.7}
        >
          <IconSymbol name="trash" size={18} color={palette.danger} />
          <Text style={[styles.menuLabel, { color: palette.danger }]}>
            Delete
          </Text>
        </TouchableOpacity>
      </Animated.View>
    </View>
  );
}

const BACKDROP_COLOR = 'rgba(0,0,0,0.25)';

const styles = StyleSheet.create({
  backdropTop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: BACKDROP_COLOR,
  },
  backdropBottom: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: BACKDROP_COLOR,
  },
  menu: {
    position: 'absolute',
    transformOrigin: 'right top',
    width: 200,
    borderRadius: BorderRadius.lg,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 12,
    elevation: 10,
    zIndex: 200,
    paddingVertical: Spacing.xs,
  },
  colorPickerRow: {
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.sm,
  },
  colorDot: {
    width: COLOR_DOT_SIZE,
    height: COLOR_DOT_SIZE,
    borderRadius: COLOR_DOT_SIZE / 2,
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.1)',
  },
  colorDotSelected: {
    borderWidth: 2,
    borderColor: 'rgba(0, 0, 0, 0.3)',
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
    gap: Spacing.md,
  },
  menuLabel: {
    fontSize: 15,
    fontWeight: '500',
  },
  separator: {
    height: 1,
    marginHorizontal: Spacing.lg,
  },
});
